<script setup lang="ts">
import type { AppInputState } from '@shared/ui/inputs'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'
import { AppRoute } from '@shared/config'
import { AppText, AppTextLink } from '@shared/ui/typography'
import { AppMultiSelect } from '@shared/ui/selects'
import { ModalConfirmation } from '@shared/ui/modals'
import { AppInput } from '@shared/ui/inputs'
import { CardAdmin } from '@shared/ui/cards'
import { BtnPrimary } from '@shared/ui/buttons'

import TeamDetailActions from './teams/TeamDetailActions.vue'
import { useTeamEditPage } from '../model/teams/team-edit.hook'

defineOptions({
  name: 'TeamDetailsPage',
})

const dayjs = useDayjs()

const {
  refName,
  refMembers,
  memberOptions,
  team,
  isNotFound,
  isSaving,
  isSaveDisabled,
  canManage,
  isDeleteOpen,
  load,
  save,
  askDelete,
  cancelDelete,
  confirmDelete,
} = useTeamEditPage()

const nameState = computed<AppInputState | undefined>(() => (refName.value.trim() ? undefined : 'error'))

const formatDateTime = (value: string | null | undefined): string =>
  value ? dayjs(value).format(DateFormat.KanbanDate) : '—'

onMounted(() => {
  void load()
})
</script>

<template>
  <div
    v-if="isNotFound"
    class="team-edit__empty"
  >
    <AppText name="body1-medium">
      Team not found
    </AppText>
    <AppText
      name="body2"
      color="slate-500"
    >
      The address does not point at a team that exists.
    </AppText>
    <AppTextLink
      name="body2"
      :href="AppRoute.Teams"
    >
      Back to teams
    </AppTextLink>
  </div>

  <div
    v-else-if="team"
    class="team-edit"
  >
    <div class="team-edit__toolbar">
      <AppText
        name="h3"
        color="slate-900"
      >
        Team / {{ team.name }}
      </AppText>
      <div class="team-edit__toolbar-actions">
        <BtnPrimary
          text="Save"
          color="emerald"
          element-width="auto"
          :disabled="isSaveDisabled || isSaving"
          @click="save"
        />
        <TeamDetailActions
          v-if="canManage"
          @click:delete="askDelete"
        />
      </div>
    </div>

    <CardAdmin
      title="Team info"
      fullwidth
    >
      <div class="team-edit__card">
        <div class="team-edit__fields">
          <div class="team-edit__info-row">
            <AppText
              name="body2-medium"
              color="slate-500"
            >
              ID
            </AppText>
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ team.id }}
            </AppText>
          </div>

          <AppInput
            v-model="refName"
            label="Name"
            placeholder="Enter name"
            :state="nameState"
            :disabled="!canManage"
          />

          <AppMultiSelect
            v-model="refMembers"
            :options="memberOptions"
            label="Members"
            placeholder="Select members"
            :disabled="!canManage"
            clearable
          />

          <div class="team-edit__info-row">
            <AppText
              name="body2-medium"
              color="slate-500"
            >
              Number of members
            </AppText>
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ team.users_count ?? refMembers.length }}
            </AppText>
          </div>

          <div class="team-edit__info-row">
            <AppText
              name="body2-medium"
              color="slate-500"
            >
              Updated at
            </AppText>
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ formatDateTime(team.updated_at) }}
            </AppText>
          </div>

          <div class="team-edit__info-row">
            <AppText
              name="body2-medium"
              color="slate-500"
            >
              Created at
            </AppText>
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ formatDateTime(team.created_at) }}
            </AppText>
          </div>
        </div>
      </div>
    </CardAdmin>
  </div>

  <ModalConfirmation
    :show="isDeleteOpen"
    mode="delete"
    title="Delete team?"
    detail="Are you sure you want to delete this team? All of data will be permanently removed. This action cannot be uncompleted."
    button-title="Delete team"
    :loading="isSaving"
    @click:confirm="confirmDelete"
    @click:cancel="cancelDelete"
  />
</template>

<style scoped lang="scss" src="./TeamDetailsPage.scss" />
